import React from "react"
import clsx from "clsx"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { t, fmtStamp } from "../helpers/format"

const TONES = {
  done:    { dot: "bg-emerald-500 text-white", line: "bg-emerald-400", title: "text-slate-900" },
  planned: { dot: "bg-slate-200 text-slate-600", line: "bg-slate-200", title: "text-slate-700" },
  rose:    { dot: "bg-rose-500 text-white", line: "bg-rose-300", title: "text-rose-700" },
  slate:   { dot: "bg-slate-700 text-white", line: "bg-slate-300", title: "text-slate-900" },
  todo:    { dot: "bg-white text-slate-300 ring-1 ring-inset ring-slate-200", line: "bg-slate-200", title: "text-slate-400" },
}

const Step = ({ icon, title, sub, tone, last }) => {
  const style = TONES[tone]
  return (
    <li className="relative flex-1 min-w-0">
      {!last && <span className={clsx("absolute top-[13px] left-[30px] right-[4px] h-[2px] rounded-full", style.line)} />}
      <span className={clsx("relative inline-grid place-items-center w-[26px] h-[26px] rounded-full text-[11px]", style.dot)}>
        <FontAwesomeIcon icon={icon} />
      </span>
      <div className={clsx("mt-1.5 text-[12px] font-semibold", style.title)}>{title}</div>
      {sub && <div className="text-[11px] text-slate-500 leading-snug pr-2">{sub}</div>}
    </li>
  )
}

const StatusTimeline = ({ day }) => {
  const entries  = day.entries || []
  const anyReal  = entries.some(entry => !entry.planned)
  const signed   = !!day.signed_at && ["signed", "approved"].includes(day.status)
  const reviewer = day.reviewed_by
  const reviewedSub = reviewer
    ? t("timeline.by_on", { name: reviewer, date: fmtStamp(day.reviewed_at) })
    : day.reviewed_at && fmtStamp(day.reviewed_at)

  const reviewStep = day.status === "approved"
    ? { icon: "check-double", title: t("timeline.approved"), sub: reviewedSub, tone: "done" }
    : day.status === "rejected"
      ? { icon: "rotate-left", title: t("timeline.rejected"), sub: reviewedSub, tone: "rose" }
      : { icon: "check-double", title: t("timeline.approval"), sub: t("timeline.waiting"), tone: "todo" }

  const steps = [
    entries.length === 0
      ? { icon: "pen", title: t("timeline.entered"), sub: t("timeline.nothing"), tone: "todo" }
      : anyReal
        ? { icon: "pen", title: t("timeline.entered"), sub: t("timeline.entries", { count: entries.length }), tone: "done" }
        : { icon: "calendar-days", title: t("timeline.planned"), sub: t("timeline.planned_hint"), tone: "planned" },
    signed
      ? { icon: "signature", title: t("timeline.signed"), sub: fmtStamp(day.signed_at), tone: "done" }
      : { icon: "signature", title: t("timeline.signature"), sub: t("timeline.not_signed"), tone: "todo" },
    reviewStep,
  ]
  if (day.closed) steps.push({ icon: "lock", title: t("timeline.closed"), sub: t("timeline.closed_hint"), tone: "slate" })

  const reopened = day.status === "draft" && reviewer

  return (
    <div className="flex flex-col gap-3">
      <ol className="flex items-start list-none p-0 m-0">
        {steps.map((step, index) => <Step key={index} {...step} last={index === steps.length - 1} />)}
      </ol>

      {day.status === "rejected" && day.review_comment && (
        <div className="flex gap-2.5 p-3 rounded-lg bg-rose-50 border border-rose-200">
          <FontAwesomeIcon icon="comment-dots" className="mt-[2px] text-rose-500" />
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-rose-600">{t("timeline.reason")}</div>
            <div className="text-[13px] text-rose-800 whitespace-pre-line">{day.review_comment}</div>
          </div>
        </div>
      )}
      {day.status === "approved" && day.review_comment && (
        <div className="flex gap-2.5 p-3 rounded-lg bg-slate-50 border border-slate-200 text-[12.5px] text-slate-700">
          <FontAwesomeIcon icon="comment" className="mt-[2px] text-slate-400" />
          <span className="whitespace-pre-line">{day.review_comment}</span>
        </div>
      )}
      {reopened && (
        <div className="flex items-center gap-2 text-[12px] text-slate-500">
          <FontAwesomeIcon icon="lock-open" className="text-slate-400" />
          {t("timeline.reopened", { name: reviewer, date: fmtStamp(day.reviewed_at) })}
        </div>
      )}
    </div>
  )
}

export default StatusTimeline
